// imports
import React, { useCallback } from 'react';
import { Close } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { Divider, Flex, TextV2 } from '@withjoy/joykit';

// local imports
import { useTranslation } from '@shared/core';
import { withWindow } from '@shared/utils/withWindow';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { AccommodationCombined } from '../../Accommodations/Accommodation.types';
import { HotelInfoSection } from './AccommodationHotelLinkInterceptor/HotelInfoSection';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog/ResponsiveDialog';
import { JoyPricingSection } from './AccommodationHotelLinkInterceptor/JoyPricingSection';
import { CTAButtonsSection } from './AccommodationHotelLinkInterceptor/CTAButtonsSection';
import { useTravelPlacesPriceComparisonFragment } from '@shared/hooks/useTravelPlacesPriceComparison';
import { ComparisonPricingSection } from './AccommodationHotelLinkInterceptor/ComparisonPricingSection';
import { useTravelPlacesPricingAndBookingDetailsQuery } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';

// interfaces
interface AccommodationsExternalHotelLinkInterceptorProps {
  isOpen: boolean;
  onClose: () => void;
  accommodation: AccommodationCombined;
  onSelectAccommodation?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

export const AccommodationsExternalHotelLinkInterceptor: React.FC<AccommodationsExternalHotelLinkInterceptorProps> = React.memo(
  ({ isOpen, onClose, accommodation, onSelectAccommodation }) => {
    const { t } = useTranslation('guestSiteCustom');
    const accTrans = t('accommodations');

    const isMobile = useIsMobileScreen();

    const { data: pricingData, loading: isPricingLoading } = useTravelPlacesPricingAndBookingDetailsQuery(
      accommodation.joyPlaceId ? [accommodation.joyPlaceId] : [],
      'joyPlace',
      !accommodation.joyPlaceId || !isOpen, // skip if no joyPlaceId OR modal is not open
    );

    const { rates: providerRates, loading: isProviderRatesLoading } = useTravelPlacesPriceComparisonFragment(accommodation?.joyPlaceId || '', !accommodation.joyPlaceId || !isOpen);

    const joyPlacePricing = pricingData?.travelPlacesPricingAndBookingDetails?.joyPlaces?.[0];

    const digits = joyPlacePricing?.pricingInfo?.nightlyBaseRate?.digits;
    const currency = joyPlacePricing?.pricingInfo?.nightlyBaseRate?.currency;
    const joyPrice = joyPlacePricing?.pricingInfo?.nightlyBaseRate?.valueInMinorUnits;

    const highestProviderRate =
      providerRates?.length > 0 ? providerRates.reduce((highest, current) => (current.priceInMinorUnits > highest.priceInMinorUnits ? current : highest)) : null;
    const originalStrikeoutPrice = joyPlacePricing?.pricingInfo?.strikeoutNightlyBaseRate?.valueInMinorUnits;

    const strikeoutPrice =
      highestProviderRate && originalStrikeoutPrice
        ? Math.max(highestProviderRate.priceInMinorUnits, originalStrikeoutPrice)
        : highestProviderRate?.priceInMinorUnits || originalStrikeoutPrice;

    const discountPercentage = strikeoutPrice && joyPrice && strikeoutPrice > joyPrice ? Math.round(((strikeoutPrice - joyPrice) / strikeoutPrice) * 100) : null;

    const handleSelectAccommodation: React.MouseEventHandler = useCallback(
      event => {
        if (onSelectAccommodation) {
          onSelectAccommodation(event as unknown as React.MouseEvent<HTMLAnchorElement>);
        }
      },
      [onSelectAccommodation],
    );

    const handleViewJoyRates = useCallback(() => {
      withWindow(global => {
        global.open(joyPlacePricing?.redirectMechanisms?.[0]?.url, '_blank');
      });
      onClose();
    }, [joyPlacePricing?.redirectMechanisms, onClose]);

    return (
      <ResponsiveDialog
        isOpen={isOpen}
        onClose={onClose}
        hideDivider={true}
        hasCloseButton={false}
        dialogOverrides={{
          Content: {
            props: {
              style: {
                width: pxToRem(540),
                position: 'relative',
                paddingLeft: pxToRem(20),
                paddingRight: pxToRem(20),
              },
            },
          },
        }}
      >
        {!isMobile && (
          <Close
            size="lg"
            onClick={onClose}
            style={{
              zIndex: 1,
              top: pxToRem(20),
              cursor: 'pointer',
              right: pxToRem(25),
              position: 'absolute',
            }}
          />
        )}
        <TextV2
          fontWeight={600}
          textAlign="center"
          fontFamily={'Inter UI'}
          typographyVariant="hed5"
          marginBottom={pxToRem(24)}
        >
          {accTrans.linkInterceptor.title()}
        </TextV2>

        <Flex
          gap={pxToRem(24)}
          border="1px solid"
          borderColor="mono3"
          paddingY={pxToRem(24)}
          flexDirection="column"
          borderRadius={pxToRem(12)}
          marginBottom={pxToRem(24)}
          paddingX={isMobile ? pxToRem(18) : pxToRem(24)}
        >
          <HotelInfoSection
            accommodation={accommodation}
            discountPercentage={discountPercentage}
          />

          <Divider />

          <JoyPricingSection
            digits={digits}
            joyPrice={joyPrice}
            currency={currency}
            isPricingLoading={isPricingLoading}
          />

          {!isPricingLoading && strikeoutPrice && currency && (
            <>
              <Divider />

              <ComparisonPricingSection
                digits={digits}
                currency={currency}
                strikeoutPrice={strikeoutPrice}
                isPricingLoading={isPricingLoading || isProviderRatesLoading}
              />
            </>
          )}
        </Flex>
        <Divider />
        <CTAButtonsSection
          accommodation={accommodation}
          onViewJoyRates={handleViewJoyRates}
          isViewJoyRatesDisabled={!joyPlacePricing?.redirectMechanisms?.[0]?.url}
          onOpenExternalLink={onSelectAccommodation ? handleSelectAccommodation : undefined}
        />
      </ResponsiveDialog>
    );
  },
);

AccommodationsExternalHotelLinkInterceptor.displayName = 'AccommodationsExternalHotelLinkInterceptor';
